/**
 * NotificationBell — the app-wide in-app notification center.
 *
 * A bell button (in the header, desktop + mobile) with an unread badge that
 * opens a dropdown listing every notification that reached the user: personal
 * ones (orders, messages, wallet, …) AND admin broadcasts. The full body is
 * shown, so a long notification that only flashed in a banner can be re-read
 * here. Opening the panel marks everything read (badge clears).
 *
 * Data comes from /api/notifications/inbox (merged feed) — see
 * server/routes/notificationInbox.ts and use-notifications-inbox.ts.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Bell, CheckCheck, Loader2, ArrowLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useNotificationInbox,
  useNotificationInboxUnread,
  useMarkInboxAllRead,
  type InboxNotification,
} from "@/hooks/use-notifications-inbox";

/** Resolve an in-app destination for a notification (explicit url wins). */
function resolveHref(item: InboxNotification): string | null {
  if (item.url) return item.url;
  switch (item.type ?? "") {
    case "new_message":
    case "new_customer_message":
      return "/messages";
    case "new_order":
    case "new_store_own_order":
      return "/virtual-shop/orders";
    case "order_to_prepare":
      return "/merchant/dropshipping-orders";
    case "new_direct_wholesale_order":
      return "/merchant/direct-wholesale-orders";
    case "new_return":
      return "/returns/incoming";
    case "return_decision":
      return "/returns";
    case "payout_complete":
    case "payout_approved":
    case "payout_hold":
    case "payout_release":
    case "credit":
    case "fee":
      return "/wallet";
    case "order_update":
    case "order_confirmed":
    case "order_shipped":
    case "order_delivered":
    case "order_accepted":
    case "order_rejected":
    case "order_cancelled":
    case "order_auto_cancelled":
    case "order_placed":
    case "delivery_otp":
    case "delivery_failed":
      return "/my-orders";
    default:
      return null;
  }
}

export default function NotificationBell({
  variant = "desktop",
  labelClassName = "hidden sm:inline text-sm",
}: {
  variant?: "desktop" | "mobile";
  /** Desktop trigger's text label; the Header passes its own breakpoints. */
  labelClassName?: string;
}) {
  const { t } = useLanguage();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const { data: unreadData } = useNotificationInboxUnread();
  const unreadCount = unreadData?.count ?? 0;

  // Only fetch the (heavier) list while the panel is open.
  const { data, isLoading } = useNotificationInbox({ enabled: open });
  const notifications = data?.notifications ?? [];

  const markAllRead = useMarkInboxAllRead();

  // Mark read shortly after the panel opens, once, only when something is
  // unread — mirrors the merchant-notifications auto-read so the badge clears.
  const autoReadRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoReadRef.current = false;
      return;
    }
    if (autoReadRef.current) return;
    if (unreadCount === 0) return;
    autoReadRef.current = true;
    const timer = setTimeout(() => {
      try {
        markAllRead.mutate();
      } catch {
        // hook surfaces its own error state
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [open, unreadCount, markAllRead]);

  const handleOpenItem = (item: InboxNotification) => {
    const href = resolveHref(item);
    if (!href) return;
    setOpen(false);
    try {
      navigate(href);
    } catch {
      // ignore navigation failures — panel is already closing
    }
  };

  const badge =
    unreadCount > 0 ? (unreadCount > 9 ? "9+" : String(unreadCount)) : null;

  const trigger =
    variant === "mobile" ? (
      <Button
        variant="ghost"
        size="sm"
        className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10 relative"
        data-testid="button-mobile-notifications"
        aria-label={t("notifications.title")}
      >
        <Bell className="w-5 h-5 text-orange-300" />
        <span className="text-[10px]">{t("notifications.title")}</span>
        {badge && (
          <span
            className="absolute top-0 right-1 bg-red-500 text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold"
            data-testid="badge-mobile-notifications-unread"
          >
            {badge}
          </span>
        )}
      </Button>
    ) : (
      <Button
        variant="ghost"
        className="text-white/90 rounded-xl relative gap-1.5"
        data-testid="button-notifications"
        aria-label={t("notifications.title")}
      >
        <Bell className="w-4 h-4 text-orange-300" />
        <span className={labelClassName}>{t("notifications.title")}</span>
        {badge && (
          <span
            className="absolute -top-1 -left-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-lg"
            data-testid="badge-notifications-unread"
          >
            {badge}
          </span>
        )}
      </Button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        dir="rtl"
        className="w-[min(92vw,22rem)] p-0 overflow-hidden"
        data-testid="popover-notifications"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-muted/40">
          <div className="flex items-center gap-2 font-bold text-foreground">
            <Bell className="w-4 h-4 text-orange-500" />
            {t("notifications.title")}
          </div>
          {notifications.some((n) => !n.isRead) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              data-testid="button-notifications-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5 ml-1" />
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-10 px-4">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="font-medium text-muted-foreground">{t("notifications.empty")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("notifications.emptyHint")}</p>
            </div>
          ) : (
            <ul className="divide-y">
              {notifications.map((item) => {
                const href = resolveHref(item);
                return (
                  <li
                    key={item.id}
                    className={`px-4 py-3 ${
                      item.isRead ? "bg-background" : "bg-orange-50 dark:bg-orange-950/20"
                    } ${href ? "cursor-pointer hover:bg-muted/60" : ""}`}
                    onClick={href ? () => handleOpenItem(item) : undefined}
                    data-testid={`notification-item-${item.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!item.isRead && (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm text-foreground break-words">
                          {item.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-0.5 break-words whitespace-pre-line">
                          {item.body}
                        </p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[11px] text-muted-foreground">
                            {item.createdAt &&
                              formatDistanceToNow(new Date(item.createdAt), {
                                addSuffix: true,
                                locale: ar,
                              })}
                          </span>
                          {href && (
                            <span className="text-[11px] text-orange-600 dark:text-orange-400 flex items-center gap-0.5 font-medium">
                              {t("notifications.open")}
                              <ArrowLeft className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
